/**
 * Нейтральные тексты клиенту: «ускоритель интернета» вместо «обход белых списков» и
 * обходов вообще (09.10.2026: этого требует платёжная касса).
 *
 * Копия таблицы `src/shared/whitelist_wording.py` (бэк). Меняешь там, меняй здесь и в
 * копиях `client-site` и `partner-site`: файл одинаковый во всех трёх фронтах.
 * Включается тумблером у бота (`hide_whitelist` в ответе авторизации / витрины), по
 * умолчанию выключен. Свой текст партнёра не трогаем (решение Глеба 01.10.2026).
 * Фразы, которые автозамена не перескажет («Без логов», «Работает там, где
 * блокируют»), меняются готовым текстом: в мини-аппе `locales/neutral.ts`, на витрине
 * `nv()` прямо в компоненте.
 *
 * В регулярках `\p{L}` с флагом `u`, а не `\w`: в JS `\w` кириллицу не ловит.
 */

const OBHOD_CASES: Record<string, string> = {
  обход: 'ускоритель',
  обхода: 'ускорителя',
  обходу: 'ускорителю',
  обходом: 'ускорителем',
  обходе: 'ускорителе',
  обходы: 'ускорители',
  обходов: 'ускорителей',
  обходам: 'ускорителям',
  обходами: 'ускорителями',
  обходах: 'ускорителях',
};

const L = '\\p{L}*';
const WL_GEN = 'белых\\s+списков';

function keepCase(src: string, dst: string): string {
  return src.charAt(0) !== src.charAt(0).toLowerCase() ? dst.charAt(0).toUpperCase() + dst.slice(1) : dst;
}

function obhod(word: string): string {
  return keepCase(word, OBHOD_CASES[word.toLowerCase()] ?? 'ускоритель');
}

const r = (pattern: string) => new RegExp(pattern, 'giu');

type Rule = [RegExp, (...m: string[]) => string];

const RULES: Rule[] = [
  [
    r(`(обход${L})\\s+(<b>)?белых\\s+списков(</b>)?\\s+и\\s+(<b>)?глушил${L}(</b>)?(\\s+мобильного\\s+интернета)?`),
    (_m, w, b1, c1, b2, c2) =>
      (b1 || b2 ? '<b>' : '') + `${obhod(w)} мобильного интернета` + (c1 || c2 ? '</b>' : ''),
  ],
  [
    r('пробива(ет|ют)\\s+белые\\s+списки\\s+и\\s+глушилки(\\s+мобильного\\s+интернета)?'),
    (m, end) => keepCase(m, `ускоря${end} мобильный интернет`),
  ],
  [r(`(тест${L})\\s+(?:обхода\\s+)?${WL_GEN}`), (_m, w) => `${w} ускорителя`],
  [r(`(сервер${L})\\s+с\\s+белыми\\s+списками`), (_m, w) => `${w} ускорителя`],
  [r(`(сервер${L})\\s+(?:для\\s+)?обход${L}\\s+${WL_GEN}`), (_m, w) => `${w} ускорителя`],
  [r(`(трафик${L})\\s+(?:(?:для|на)\\s+)?обход${L}\\s+${WL_GEN}`), (_m, w) => `${w} ускорителя`],
  [r(`(режим${L})\\s+${WL_GEN}`), (_m, w) => `${w} ускорителя`],
  [
    r('обход(ит|ят)\\s+белые\\s+списки'),
    (m, end) => keepCase(m, `ускоря${end.toLowerCase() === 'ит' ? 'ет' : 'ют'} интернет`),
  ],
  [r(`(обход${L})\\s+${WL_GEN}`), (_m, w) => `${obhod(w)} интернета`],
  [r('обход\\s+бс(?!\\p{L})'), (m) => keepCase(m, 'ускоритель')],
  // Название спецтарифа поставщика «Белые Списки | 3GB» (см. бэк-копию таблицы).
  [r('белые\\s+списки(?=\\s*\\|)'), (m) => keepCase(m, 'доп. серверы')],
  [r(`для\\s+${WL_GEN}`), (m) => keepCase(m, 'для ускорителя')],
  [r(WL_GEN), (m) => keepCase(m, 'ускорителя')],
  [r('белым\\s+спискам'), (m) => keepCase(m, 'ускорителю')],
  [r('белыми\\s+списками'), (m) => keepCase(m, 'ускорителем')],
  [r('белых\\s+списках'), (m) => keepCase(m, 'ускорителе')],
  [r('белые\\s+списки'), (m) => keepCase(m, 'ускоритель')],
  [r('белый\\s+список'), (m) => keepCase(m, 'ускоритель')],
  // «на специальных whitelisted-серверах»
  [r(`whitelisted[-\\s]+(сервер${L})`), (_m, w) => `${w} ускорителя`],
  // «Обход» сам по себе: «Трафик обхода», «Осталось на обход». Только существительное.
  // Без lookbehind: старые iOS его не знают, и мини-апп упал бы на загрузке модуля.
  [
    r('(^|[^\\p{L}])(обход(?:а|у|ом|е|ы|ов|ам|ами|ах)?)(?!\\p{L})'),
    (_m, pre, w) => `${pre}${obhod(w)}`,
  ],
];

const ANY = /бел\p{L}*\s+спис|обход|whitelisted/iu;

/** Заменить упоминания белых списков. Текст без них возвращается как есть. */
export function softenWhitelist(text: string): string {
  if (!text || !ANY.test(text)) return text;
  let out = text;
  for (const [pattern, repl] of RULES) {
    out = out.replace(pattern, (...args: unknown[]) => {
      const groups = args.slice(0, -2).map((g) => (typeof g === 'string' ? g : ''));
      return repl(...groups);
    });
  }
  return out;
}

// Флаг на всю страницу. Витрина ставит его, когда пришли данные сайта (до первой
// отрисовки разделов), и дальше `ww()` его подхватывает без протаскивания параметра
// через чистые функции.
let pageOn = false;

export function setWhitelistWordingOn(on: boolean): void {
  pageOn = on;
}

/** Заменить, если тумблер включён: явно (`ww(text, on)`) или флагом страницы. */
export function ww(text: string, on: boolean = pageOn): string {
  return on ? softenWhitelist(text) : text;
}

/** Готовый нейтральный вариант, если тумблер включён: `nv('Без логов', 'Стабильно')`. */
export function nv<T>(normal: T, neutral: T, on: boolean = pageOn): T {
  return on ? neutral : normal;
}
